/**
 * Seen-job-id tracker
 *
 * Tiny bounded dedup set shared by the SSE `sync_completed` consumers
 * (useInvalidateTransactionsOnSyncCompleted, usePostSyncSummaryToast). A
 * replayed event (SSE reconnect, StrictMode double-mount) must not trigger
 * duplicate cache invalidations or stack the same toast twice.
 *
 * Instances are module-level singletons in each consumer hook so they survive
 * re-renders and double-mounts. Bounded (FIFO eviction) so a long-lived
 * session does not grow without limit.
 */

export interface SeenJobIdTracker {
  /** True when this jobId was already marked seen (and not yet evicted). */
  hasSeen: (jobId: string) => boolean
  /** Record the jobId, evicting the oldest entry once the limit is reached. */
  markSeen: (jobId: string) => void
  /** Clear all entries — intended for test isolation only. */
  reset: () => void
}

export function createSeenJobIdTracker(limit = 500): SeenJobIdTracker {
  const seenJobIds = new Set<string>()

  return {
    hasSeen: (jobId: string): boolean => seenJobIds.has(jobId),
    markSeen: (jobId: string): void => {
      if (seenJobIds.size >= limit) {
        // Set iteration order is insertion order → first value is the oldest.
        const oldest = seenJobIds.values().next().value
        if (oldest !== undefined) seenJobIds.delete(oldest)
      }
      seenJobIds.add(jobId)
    },
    reset: (): void => {
      seenJobIds.clear()
    },
  }
}
